import { Stack, Typography } from '@mui/material';
import { Accommodation, PerformerRequest } from '@silbo/core';
import { isYesterday } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ArchivedRequestCard from '../components/ArchivedRequestCard';
import PageContainer from '../components/PageContainer';

interface ArchivesPageProps {
  requests: PerformerRequest[];
  accommodations: Accommodation[];
}

const ArchivesPage = ({ requests, accommodations }: ArchivesPageProps) => {
  const { t } = useTranslation('bed-management');

  const accommodationByRequestId = useMemo(() => {
    const map = new Map();

    for (const accommodation of accommodations) {
      if (accommodation.basedOn) {
        map.set(accommodation.basedOn.id, accommodation);
      }
    }
    return map;
  }, [accommodations]);

  const { todayRequests, yesterdayRequests } = useMemo(() => {
    const todayRequests: PerformerRequest[] = [];
    const yesterdayRequests: PerformerRequest[] = [];

    for (const request of requests) {
      if (!request.fulfilledAt) {
        continue;
      }
      if (isYesterday(request.fulfilledAt)) {
        yesterdayRequests.push(request);
      } else {
        todayRequests.push(request);
      }
    }
    return { todayRequests, yesterdayRequests };
  }, [requests]);

  return (
    <PageContainer title={t('archive.title')} badgeCount={requests.length}>
      <Stack spacing={3}>
        {requests.length === 0 ? (
          <Typography color="text.secondary">{t('archive.noArchivedRequests')}</Typography>
        ) : (
          <Stack spacing={4}>
            {todayRequests.length > 0 && (
              <Stack spacing={3}>
                <Typography fontWeight={500}>{t('archive.today')}</Typography>
                {todayRequests.map((request) => (
                  <ArchivedRequestCard
                    key={request.id}
                    request={request}
                    accommodation={accommodationByRequestId.get(request.request.id)}
                  />
                ))}
              </Stack>
            )}
            {yesterdayRequests.length > 0 && (
              <Stack spacing={3}>
                <Typography fontWeight={500}>{t('archive.yesterday')}</Typography>
                {yesterdayRequests.map((request) => (
                  <ArchivedRequestCard
                    key={request.id}
                    request={request}
                    accommodation={accommodationByRequestId.get(request.request.id)}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </PageContainer>
  );
};

export default ArchivesPage;
